'use client';
// ──────────────────────────────────────────────────────
// ProductCardOverlay — Full-screen product display overlay
// White theme: clean surfaces, teal accents, subtle shadows
//
// scanPhase integration:
//   ai_processing — card is NOT visible (AiScanLoader shown instead)
//   revealing     — card fades/slides in with premium spring reveal
//   done / error  — card remains visible
// ──────────────────────────────────────────────────────
import { useState, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import type { ScanResponse, UpsellCard } from '@/lib/types';
import type { ScanPhase } from '@/hooks/useTwoPhaseScan';
import { AuthenticityCard } from './AuthenticityCard';

/** Normalize bodyAr to a single string for rendering */
export function renderBody(bodyAr: string | string[]): string {
    if (Array.isArray(bodyAr)) return bodyAr.join('\n');
    return bodyAr;
}

// ── Product Image — shimmer placeholder + fade-in ────
function ProductImage({ src, alt }: { src: string; alt: string }) {
    const [loaded, setLoaded] = useState(false);
    const [errored, setErrored] = useState(false);

    const onLoad = useCallback(() => setLoaded(true), []);
    const onError = useCallback(() => setErrored(true), []);

    if (errored) return null;

    return (
        <div className="mb-3 relative rounded-xl overflow-hidden" style={{ height: 192, background: '#ffffff' }}>
            {/* Shimmer placeholder — visible until image loads */}
            {!loaded && (
                <div className="absolute inset-0 shimmer rounded-xl" />
            )}
            {/* Actual image — starts invisible, fades in */}
            {/* eslint-disable-next-line @next/next/no-img-element */}
            <img
                src={src}
                alt={alt}
                className="w-full h-full object-contain rounded-xl"
                style={{
                    opacity: loaded ? 1 : 0,
                    transition: 'opacity 300ms ease',
                    background: '#ffffff',
                }}
                onLoad={onLoad}
                onError={onError}
                loading="eager"
                decoding="async"
            />
        </div>
    );
}

interface ProductCardProps {
    data: ScanResponse | null;
    loading: boolean;
    error: string | null;
    /** Phase from useTwoPhaseScan — controls when card is visible and how it enters */
    scanPhase?: ScanPhase;
    onDismiss: () => void;
}

export function ProductCardOverlay({ data, loading, error, scanPhase, onDismiss }: ProductCardProps) {
    // Card is visible only when we are past the AI processing phase
    const revealing = scanPhase === 'revealing' || scanPhase === 'done' || scanPhase === 'error';
    // Legacy fallback: if scanPhase not provided, fall back to original logic
    const visible = scanPhase ? (revealing && (!!data || !!error)) : (loading || !!data || !!error);

    return (
        <AnimatePresence>
            {visible && (
                <>
                    {/* Backdrop */}
                    <motion.div
                        key="backdrop"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="fixed inset-0 z-[80]"
                        style={{ background: 'rgba(0,0,0,0.25)', backdropFilter: 'blur(8px)' }}
                        onClick={onDismiss}
                    />

                    {/* Card — soft slide-up entrance after AI loader fade-out */}
                    <motion.div
                        key="card"
                        initial={{ opacity: 0, y: 32, scale: 0.98 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        exit={{ opacity: 0, y: 24, scale: 0.97 }}
                        transition={{ type: 'spring', damping: 28, stiffness: 260, duration: 0.5 }}
                        className="fixed inset-x-4 top-[8%] bottom-[8%] z-[90] flex flex-col rounded-3xl overflow-hidden"
                        style={{
                            background: '#ffffff',
                            border: '1px solid #e5e7eb',
                            boxShadow: '0 20px 60px rgba(0,0,0,0.12), 0 4px 16px rgba(0,0,0,0.06)',
                            maxWidth: '600px',
                            margin: '0 auto',
                        }}
                    >
                        {/* Header */}
                        <div className="flex items-center justify-between px-6 py-4"
                            style={{ borderBottom: '1px solid #f0f0f0' }}>
                            <div className="flex items-center gap-2">
                                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#0ea5a5" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                                    <path d="M3 7V5a2 2 0 012-2h2" />
                                    <path d="M17 3h2a2 2 0 012 2v2" />
                                    <path d="M21 17v2a2 2 0 01-2 2h-2" />
                                    <path d="M7 21H5a2 2 0 01-2-2v-2" />
                                </svg>
                                <span className="text-sm font-bold" style={{ color: '#1a1a2e' }}>نتيجة المسح</span>
                            </div>
                            <button onClick={onDismiss} className="w-8 h-8 rounded-lg flex items-center justify-center hover:bg-slate-100 transition" style={{ color: '#94a3b8' }}>
                                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
                                </svg>
                            </button>
                        </div>

                        {/* Content */}
                        <div className="flex-1 overflow-y-auto px-6 py-5 space-y-4" dir="rtl">
                            {/* Loading skeleton suppressed — AiScanLoader handles loading UX */}
                            {error && <ErrorState message={error} />}
                            {data && !loading && <ProductContent data={data} />}
                        </div>

                        {/* Footer — tap to dismiss */}
                        <div className="px-6 py-3 text-center" style={{ borderTop: '1px solid #f0f0f0' }}>
                            <button onClick={onDismiss}
                                className="px-6 py-2.5 rounded-xl text-sm font-medium transition-all active:scale-95"
                                style={{ background: '#0ea5a5', color: '#ffffff', boxShadow: '0 2px 8px rgba(14,165,165,0.2)' }}>
                                إغلاق — امسح منتج آخر
                            </button>
                        </div>
                    </motion.div>
                </>
            )}
        </AnimatePresence>
    );
}

// ── Error state ──────────────────────────────────────
function ErrorState({ message }: { message: string }) {
    return (
        <div className="flex flex-col items-center justify-center gap-3 py-12">
            <div className="w-14 h-14 rounded-2xl flex items-center justify-center" style={{ background: 'rgba(239,68,68,0.06)', border: '1px solid rgba(239,68,68,0.15)' }}>
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#EF4444" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="12" cy="12" r="10" />
                    <line x1="12" y1="8" x2="12" y2="12" />
                    <line x1="12" y1="16" x2="12.01" y2="16" />
                </svg>
            </div>
            <p className="text-sm text-center font-medium" style={{ color: '#1a1a2e' }}>{message}</p>
        </div>
    );
}

// ── Category badge ───────────────────────────────────
const CATEGORY_COLORS: Record<string, { bg: string; text: string; border: string }> = {
    skincare: { bg: '#f0fafa', text: '#0d8f8f', border: '#d0eded' },
    suncare: { bg: '#fffbeb', text: '#b45309', border: '#fde68a' },
    medical: { bg: '#fef2f2', text: '#dc2626', border: '#fecaca' },
};

export function CategoryBadge({ category }: { category: string }) {
    const colors = CATEGORY_COLORS[category.toLowerCase()] || CATEGORY_COLORS.skincare;
    return (
        <span className="inline-block px-2.5 py-0.5 rounded-full text-xs font-medium"
            style={{ background: colors.bg, color: colors.text, border: `1px solid ${colors.border}` }}>
            {category}
        </span>
    );
}

// ── Product content ──────────────────────────────────
export function ProductContent({ data }: { data: ScanResponse }) {
    const heroCards = data.cards.filter(c => c.type === 'hero');
    const factCards = data.cards.filter(c => c.type === 'fact' || !c.type);
    const disclaimerCards = data.cards.filter(c => c.type === 'disclaimer');
    const p = data.product;

    // Enrichment arrays (prefer enrichment, fallback empty)
    const benefits = p?.benefitsAr?.length ? p.benefitsAr : [];
    const usage = p?.howToUseAr?.length ? p.howToUseAr : [];
    const ingredients = p?.ingredientsAr?.length ? p.ingredientsAr : [];
    const warnings = p?.warningsAr?.length ? p.warningsAr : [];

    return (
        <>
            {/* Product Identity Header */}
            {p && (
                <div className="rounded-2xl px-5 py-4" style={{ background: '#f8fafa', border: '1px solid #e0f0f0' }}>
                    {/* Product image with shimmer + fade-in */}
                    {p.imageUrl && /^https?:\/\//.test(p.imageUrl) && (
                        <ProductImage src={p.imageUrl} alt={p.nameAr} />
                    )}

                    {/* Marketing headline */}
                    {p.marketingHeadlineAr && (
                        <p className="text-xs font-semibold mb-1" style={{ color: '#0d8f8f' }}>
                            {p.marketingHeadlineAr}
                        </p>
                    )}

                    <h2 className="text-xl font-bold mb-1" style={{ color: '#1a1a2e' }}>
                        {p.nameAr}
                    </h2>
                    <div className="flex items-center gap-2 flex-wrap">
                        {p.brand && (
                            <span className="text-sm" style={{ color: '#64748b' }}>{p.brand}</span>
                        )}
                        {p.category && (
                            <CategoryBadge category={p.category} />
                        )}
                    </div>
                    {/* Price display */}
                    {p.price != null && p.price > 0 && (
                        <div className="mt-2 flex items-baseline gap-1.5">
                            <span className="text-2xl font-extrabold" style={{ color: '#0d8f8f' }}>
                                {p.price.toFixed(2)}
                            </span>
                            <span className="text-sm font-semibold" style={{ color: '#0d8f8f' }}>ريال</span>
                            <span className="text-xs" style={{ color: '#94a3b8', marginRight: '4px' }}>شامل الضريبة</span>
                        </div>
                    )}
                </div>
            )}

            {/* Description — enrichment-first, source fallback */}
            {(() => {
                const desc = p?.marketingDescriptionAr?.trim() || p?.descriptionAr?.trim();
                return desc ? (
                    <div className="rounded-2xl px-5 py-4" style={{ background: '#f8f9fb', border: '1px solid #f0f0f0' }}>
                        <div className="flex items-center gap-2 mb-2">
                            <span>📋</span>
                            <h3 className="text-sm font-bold" style={{ color: '#0d8f8f' }}>وصف سريع</h3>
                        </div>
                        <p className="text-sm leading-relaxed" style={{ color: '#475569' }}>
                            {desc}
                        </p>
                    </div>
                ) : null;
            })()}

            {/* ── Enrichment Structured Sections ── */}

            {/* Benefits — أهم المميزات */}
            {benefits.length > 0 && (
                <div className="rounded-2xl px-5 py-4" style={{ background: '#f8fafa', border: '1px solid #e0f0f0' }}>
                    <div className="flex items-center gap-2 mb-2">
                        <span>✨</span>
                        <h3 className="text-sm font-bold" style={{ color: '#0d8f8f' }}>أهم المميزات</h3>
                    </div>
                    <ul className="space-y-1.5">
                        {benefits.map((b, i) => (
                            <li key={i} className="text-sm leading-relaxed flex gap-2" style={{ color: '#334155' }}>
                                <span className="text-xs mt-1" style={{ color: '#0d8f8f' }}>●</span>
                                <span>{b}</span>
                            </li>
                        ))}
                    </ul>
                </div>
            )}

            {/* Usage — طريقة الاستخدام */}
            {usage.length > 0 && (
                <div className="rounded-2xl px-5 py-4" style={{ background: '#f8f9fb', border: '1px solid #f0f0f0' }}>
                    <div className="flex items-center gap-2 mb-2">
                        <span>💡</span>
                        <h3 className="text-sm font-bold" style={{ color: '#0d8f8f' }}>طريقة الاستخدام</h3>
                    </div>
                    <ol className="space-y-1.5">
                        {usage.map((step, i) => (
                            <li key={i} className="text-sm leading-relaxed flex gap-2" style={{ color: '#475569' }}>
                                <span className="text-xs font-bold mt-0.5" style={{ color: '#0d8f8f', minWidth: '16px' }}>{i + 1}.</span>
                                <span>{step}</span>
                            </li>
                        ))}
                    </ol>
                </div>
            )}

            {/* Ingredients — المكونات */}
            {ingredients.length > 0 && (
                <div className="rounded-2xl px-5 py-4" style={{ background: '#f8f9fb', border: '1px solid #f0f0f0' }}>
                    <div className="flex items-center gap-2 mb-2">
                        <span>⚗️</span>
                        <h3 className="text-sm font-bold" style={{ color: '#0d8f8f' }}>المكونات</h3>
                    </div>
                    <div className="flex flex-wrap gap-1.5">
                        {ingredients.map((ing, i) => (
                            <span key={i} className="inline-block px-2.5 py-1 rounded-lg text-xs" style={{ background: '#f0fafa', color: '#0d8f8f', border: '1px solid #d0eded' }}>
                                {ing}
                            </span>
                        ))}
                    </div>
                </div>
            )}

            {/* Warnings — التنبيهات والتحذيرات */}
            {warnings.length > 0 && (
                <div className="rounded-2xl px-5 py-4" style={{ background: '#fffbeb', border: '1px solid #fde68a' }}>
                    <div className="flex items-center gap-2 mb-2">
                        <span>⚠️</span>
                        <h3 className="text-sm font-bold" style={{ color: '#b45309' }}>التنبيهات والتحذيرات</h3>
                    </div>
                    <ul className="space-y-1">
                        {warnings.map((w, i) => (
                            <li key={i} className="text-xs leading-relaxed" style={{ color: '#92400e' }}>
                                • {w}
                            </li>
                        ))}
                    </ul>
                </div>
            )}

            {/* Hero cards from LLM (if any) */}
            {heroCards.map((card, i) => (
                <div key={`hero-${i}`} className="rounded-2xl px-5 py-4" style={{ background: '#f8fafa', border: '1px solid #e0f0f0' }}>
                    <div className="flex items-center gap-2 mb-2">
                        <span>✨</span>
                        <h3 className="text-sm font-bold" style={{ color: '#0d8f8f' }}>{card.titleAr}</h3>
                    </div>
                    <p className="text-sm leading-relaxed whitespace-pre-line" style={{ color: '#334155' }}>
                        {renderBody(card.bodyAr)}
                    </p>
                </div>
            ))}

            {/* Mode badge */}
            {data.mode === 'UNKNOWN_BARCODE' && (
                <div className="px-4 py-2 rounded-xl text-xs font-medium text-center" style={{ background: '#fffbeb', border: '1px solid #fde68a', color: '#b45309' }}>
                    هذا المنتج غير مسجل حالياً في قاعدة البيانات
                </div>
            )}

            {data.mode === 'FOUND_INACTIVE' && (
                <div className="px-4 py-2 rounded-xl text-xs font-medium text-center" style={{ background: '#fffbeb', border: '1px solid #fde68a', color: '#b45309' }}>
                    ⏸️ المنتج موقوف حالياً — تواصل مع موظفي القسم
                </div>
            )}

            {data.mode === 'FOUND_OUT_OF_STOCK' && (
                <div className="px-4 py-2 rounded-xl text-xs font-medium text-center" style={{ background: '#fef2f2', border: '1px solid #fecaca', color: '#dc2626' }}>
                    📦 المنتج غير متوفر بالمخزون حالياً
                </div>
            )}

            {/* Fact cards — additional info from rules/LLM */}
            {factCards.map((card, i) => (
                <div key={`fact-${i}`} className="rounded-2xl px-5 py-4" style={{ background: '#f8f9fb', border: '1px solid #f0f0f0' }}>
                    <div className="flex items-center gap-2 mb-2">
                        {card.icon && <span>{card.icon}</span>}
                        {!card.icon && <span>💡</span>}
                        <h3 className="text-sm font-bold" style={{ color: '#0d8f8f' }}>{card.titleAr}</h3>
                    </div>
                    <p className="text-sm leading-relaxed whitespace-pre-line" style={{ color: '#475569' }}>
                        {renderBody(card.bodyAr)}
                    </p>
                </div>
            ))}

            {/* Upsells */}
            {data.upsells.length > 0 && (
                <div className="space-y-2">
                    <h3 className="text-sm font-bold" style={{ color: '#0d8f8f' }}>🛍️ منتجات مقترحة</h3>
                    {data.upsells.map((u: UpsellCard, i: number) => (
                        <div key={i} className="rounded-xl px-4 py-3" style={{ background: '#f8fafa', border: '1px solid #e0f0f0' }}>
                            <span className="text-sm font-medium" style={{ color: '#334155' }}>{u.nameAr || u.productId}</span>
                            {u.reasonAr && <p className="text-xs mt-0.5" style={{ color: '#94a3b8' }}>{u.reasonAr}</p>}
                        </div>
                    ))}
                </div>
            )}

            {/* Disclaimers */}
            {disclaimerCards.map((card, i) => (
                <div key={`disc-${i}`} className="px-4 py-3 rounded-xl text-xs" style={{ background: '#fffbeb', border: '1px solid #fde68a', color: '#b45309' }}>
                    ⚠️ {renderBody(card.bodyAr)}
                </div>
            ))}

            {data.disclaimer && (
                <p className="text-xs text-center px-4" style={{ color: '#94a3b8' }}>{data.disclaimer}</p>
            )}

            {/* Authenticity Card — shown when product is identified */}
            {data.product && data.mode !== 'UNKNOWN_BARCODE' && data.mode !== 'FOUND_INACTIVE' && data.mode !== 'FOUND_OUT_OF_STOCK' && (
                <AuthenticityCard productId={data.product.id} />
            )}
        </>
    );
}
